import { useEffect } from 'react';
import type { BoardDoc } from '../doc/BoardDoc';
import { EDITABLE_TYPES } from '../doc/schema';
import { bringForward, bringToFront, sendBackward, sendToBack } from '../doc/zorder';
import {
  copySelection,
  createLinkAt,
  createNoteAt,
  cutSelection,
  looksLikeUrl,
  deleteSelection,
  duplicateSelection,
  nudgeSelection,
  pasteClipboard,
  selectAll,
  viewCenter,
  zoomBy,
  zoomTo,
  zoomToFit,
  zoomToSelection,
} from './actions';
import { importImageFiles, pickImageFiles } from './importImages';
import { boardIdFromUrl, createBoardCardAt } from './boardCard';
import { clipboardFromEvent, recentClipboard, rememberClipboard, snapshotForClipboard, writeToClipboardEvent } from './clipboard';
import { canvasUnderMouse, paneElement } from './paneRegistry';
import { createNotesFromList, pastedList, PASTED_NOTE_MAX } from './noteList';
import { flash } from './flash';
import type { PaneStore } from './paneStore';
import { setBrushSettings, setPenMode, setToolSettings, toolSettings, type PenMode } from './toolSettings';
import { groupSelection, toggleLockSelection, ungroupSelection } from './arrange';
import { enterPaint, isSheet, redoAction, sheetToolPressed, undoAction } from './paint/session';
import { BRUSH_SIZE_RANGE, type BrushKind } from './paint/brushes';
import { rasterStore } from './paint/rasterStore';

/** Draw tool modes by key. B is the brush pen; Shift+B the table. */
const PEN_KEYS: Record<string, PenMode> = {
  p: 'pen',
  u: 'fineliner',
  b: 'brush',
  m: 'highlighter',
  g: 'fill',
  e: 'eraser',
  l: 'lasso',
};

/** The pane that last received a pointer event (or a paste); keyboard shortcuts go there. */
let activePane: PaneStore | null = null;
export const setActivePane = (p: PaneStore) => {
  activePane = p;
};

function isTextTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
}

export function useKeyboard(board: BoardDoc, pane: PaneStore, readOnly = false): void {
  useEffect(() => {
    // The most recently mounted pane takes the keyboard until a pointer lands on another one.
    activePane = pane;
    const ctx = { board, pane };

    const onKeyDown = (e: KeyboardEvent) => {
      if (activePane !== pane) return;
      const s = pane.getState();
      if (isTextTarget(e.target)) {
        // an editor that used Escape itself (closing its completions) keeps the focus
        if (e.key === 'Escape' && !e.defaultPrevented) (e.target as HTMLElement).blur();
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const NAV_KEYS = new Set(['+', '=', '-', '0', '1', '2', 'v', 'h', 'a', 'q', 'j', 'escape', '?', 'tab']);
      if (readOnly && e.code !== 'Space' && !NAV_KEYS.has(key)) return;

      if (e.code === 'Space' && !e.repeat) {
        s.setSpaceHeld(true);
        e.preventDefault();
        return;
      }

      // Tab hides the interface and brings it back (Shift+Tab still walks the focus)
      if (key === 'tab' && !mod && !e.shiftKey && !e.altKey) {
        s.setUiHidden(!s.uiHidden);
        e.preventDefault();
        return;
      }

      const painting = s.tool === 'sheet' && s.paintSheetId !== null;
      if (painting && mod && s.paintSheetId) {
        // the pixel selection takes over select all and deselect (the board's duplicate)
        if (key === 'a') {
          rasterStore(board).selectAll(s.paintSheetId);
          e.preventDefault();
          return;
        }
        if (key === 'd') {
          rasterStore(board).setSelection(s.paintSheetId, null);
          e.preventDefault();
          return;
        }
      }
      if (painting && !mod && s.paintSheetId) {
        // brushes and sizes while painting; everything else on the board waits
        const sheetId = s.paintSheetId;
        const store = rasterStore(board);
        const pick = (brush: BrushKind) => {
          setToolSettings({ brush });
          e.preventDefault();
        };
        switch (key) {
          case 'escape':
            // first the pixel selection (pixels on the move land), then paint mode itself
            if (store.selection(sheetId) || store.floating()) {
              store.setSelection(sheetId, null);
              return;
            }
            s.setTool('select');
            return;
          case 'x':
            return pick('pixel');
          case 't':
            return pick('halftone');
          case 'a':
            return pick('spray');
          case 'l':
            return pick('lasso');
          case 'v':
            return pick('move');
          case 'arrowleft':
          case 'arrowright':
          case 'arrowup':
          case 'arrowdown': {
            // the move tool nudges the pixels; with any other brush the arrows do nothing here
            e.preventDefault();
            if (toolSettings.getState().brush !== 'move') return;
            const live = store.active(sheetId);
            const def = live ? store.layerDef(sheetId, live.layerId) : null;
            if (!live || !def || def.locked || !def.visible || !store.lift(live)) return;
            const f = store.floating();
            if (!f) return;
            const d = e.shiftKey ? 10 : 1;
            store.moveFloat(f.x + (key === 'arrowleft' ? -d : key === 'arrowright' ? d : 0), f.y + (key === 'arrowup' ? -d : key === 'arrowdown' ? d : 0));
            return;
          }
          case 'p':
            return pick('pencil');
          case 'f':
            return pick('fineliner');
          case 'b':
            return pick('brush');
          case 'e':
            return pick('eraser');
          case 'g':
            return pick('fill');
          case 'i':
            return pick('picker');
          case '[':
          case ']': {
            const ts = toolSettings.getState();
            const [min, max] = BRUSH_SIZE_RANGE[ts.brush];
            const cur = ts.brushes[ts.brush].size;
            const step = Math.max(1, Math.round(cur * 0.2));
            const size = Math.max(min, Math.min(max, key === '[' ? cur - step : cur + step));
            setBrushSettings(ts.brush, { size });
            e.preventDefault();
            return;
          }
          case 'delete':
          case 'backspace': {
            // never the sheet itself while painting on it: the selected pixels, if any
            const live = store.active(sheetId);
            const def = live ? store.layerDef(sheetId, live.layerId) : null;
            if (live && def && !def.locked && def.visible) store.eraseSelection(live);
            e.preventDefault();
            return;
          }
        }
      }

      if (mod) {
        switch (key) {
          case 'z':
            if (e.shiftKey) void redoAction(ctx);
            else void undoAction(ctx);
            e.preventDefault();
            return;
          case 'y':
            void redoAction(ctx);
            e.preventDefault();
            return;
          case 'a':
            selectAll(ctx);
            e.preventDefault();
            return;
          case 'c':
            // no preventDefault: the browser's copy event follows and fills the system clipboard
            void copySelection(ctx);
            return;
          case 'x':
            void cutSelection(ctx);
            return;
          case 'v':
            // handled by the 'paste' event so system-clipboard images work too
            return;
          case 'd':
            void duplicateSelection(ctx);
            e.preventDefault();
            return;
          case 'g':
            if (e.shiftKey) ungroupSelection(ctx);
            else groupSelection(ctx);
            e.preventDefault();
            return;
          case 'l':
            toggleLockSelection(ctx);
            e.preventDefault();
            return;
          case "'":
            setToolSettings({ snap: !toolSettings.getState().snap });
            e.preventDefault();
            return;
          case '0':
            zoomTo(pane, 1);
            e.preventDefault();
            return;
          case '=':
          case '+':
            zoomBy(pane, 1.25);
            e.preventDefault();
            return;
          case '-':
            zoomBy(pane, 0.8);
            e.preventDefault();
            return;
          case '[':
            sendToBack(board, new Set(s.selection));
            e.preventDefault();
            return;
          case ']':
            bringToFront(board, new Set(s.selection));
            e.preventDefault();
            return;
        }
        return;
      }

      switch (e.key) {
        case 'Delete':
        case 'Backspace':
          deleteSelection(ctx);
          e.preventDefault();
          return;
        case '?':
          window.dispatchEvent(new Event('vds:shortcuts'));
          return;
        case 'Escape':
          // first press clears what is selected or being edited; a second one returns to Select
          if (s.selection.size || s.editingId) {
            s.clearSelection();
            s.setEditing(null);
          } else {
            s.setTool('select');
          }
          return;
        case 'ArrowLeft':
        case 'ArrowRight':
        case 'ArrowUp':
        case 'ArrowDown': {
          if (!s.selection.size) return;
          const d = e.shiftKey ? 10 : 1;
          const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
          const dy = e.key === 'ArrowUp' ? -d : e.key === 'ArrowDown' ? d : 0;
          nudgeSelection(ctx, dx, dy);
          e.preventDefault();
          return;
        }
        case '[':
          sendBackward(board, new Set(s.selection));
          return;
        case ']':
          bringForward(board, new Set(s.selection));
          return;
        case '+':
        case '=':
          zoomBy(pane, 1.25);
          return;
        case '-':
          zoomBy(pane, 0.8);
          return;
        case '0':
          zoomTo(pane, 1);
          return;
        case '1':
          zoomToFit(ctx);
          return;
        case '2':
          zoomToSelection(ctx);
          return;
      }

      switch (key) {
        case 'q':
          // the cutout drawer; with Shift the selection goes into it
          if (e.shiftKey) {
            if (s.selection.size) s.requestDrawerSave();
          } else s.setDrawerOpen(!s.drawerOpen);
          return;
        case 'j':
          // the frame index
          s.setFramesOpen(!s.framesOpen);
          return;
        case 'v':
          s.setTool('select');
          return;
        case 'h':
          s.setTool('pan');
          return;
        case 'n':
          s.setTool('note');
          return;
        case 't':
          s.setTool('title');
          return;
        case 'x':
          s.setTool('richtext');
          return;
        case 'b':
          if (e.shiftKey) {
            s.setTool('table');
            return;
          }
          setPenMode(PEN_KEYS[key]!);
          s.setTool('draw');
          return;
        case 'd':
          s.setTool('todo');
          return;
        case 'k':
          s.setTool('kanban');
          return;
        case 's':
          s.setTool('shape');
          return;
        case 'a':
          // A also passes the viewers' key filter (for Ctrl+A): they cannot draw connectors
          if (!readOnly) s.setTool('connector');
          return;
        case 'f':
          s.setTool('frame');
          return;
        case 'c':
          s.setTool('cutout');
          return;
        case 'r':
          sheetToolPressed(ctx);
          return;
        case 'p':
        case 'u':
        case 'm':
        case 'g':
        case 'e':
        case 'l':
          setPenMode(PEN_KEYS[key]!);
          s.setTool('draw');
          return;
        case 'i':
          void pickImageFiles().then((files) => importImageFiles(ctx, files));
          return;
        case 'enter': {
          const [only] = s.selection;
          if (only && s.selection.size === 1) {
            const item = board.index.get(only);
            if (item && EDITABLE_TYPES.has(item.type)) s.setEditing(only);
            else if (isSheet(item) && !board.isLocked(item)) enterPaint(ctx, item.id);
          }
          return;
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') pane.getState().setSpaceHeld(false);
    };

    const onPaste = (e: ClipboardEvent) => {
      if (isTextTarget(e.target)) return;
      // The pane whose canvas is under the mouse takes the paste and lands it at the cursor (and
      // the keyboard with it, so undo or delete act on what was pasted). With the mouse over a
      // panel or outside the window, the active pane pastes into its view.
      const canvas = canvasUnderMouse();
      if ((canvas?.pane ?? activePane) !== pane || readOnly) return;
      activePane = pane;
      const at = canvas?.world;
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length) {
        e.preventDefault();
        void importImageFiles(ctx, files, at);
        return;
      }
      // items copied on a board: this tab, another tab, or the system clipboard
      const items = clipboardFromEvent(e);
      if (items) {
        e.preventDefault();
        void pasteClipboard(ctx, at, items);
        return;
      }
      const text = (e.clipboardData?.getData('text/plain') ?? '').trim();
      if (!text) return;
      e.preventDefault();
      const where = at ?? viewCenter(pane);
      // a link to one of this app's boards becomes a board card rather than a website card
      const linkedBoard = board.remote ? boardIdFromUrl(text) : null;
      if (linkedBoard) {
        createBoardCardAt(ctx, linkedBoard, where);
        return;
      }
      if (looksLikeUrl(text)) {
        createLinkAt(ctx, text, where);
        return;
      }
      // a bullet or numbered list becomes one note per item, laid out as the list reads
      const list = pastedList(e.clipboardData);
      if (list) {
        createNotesFromList(ctx, list, where);
        const el = paneElement(pane);
        if (el) flash(el, `List pasted as ${list.length} sticky notes`);
        return;
      }
      // any other text lands as a sticky note
      createNoteAt(ctx, where, false, text.slice(0, PASTED_NOTE_MAX));
    };

    // Copy/cut: fill the system clipboard with the items, so another tab (or a later session)
    // can paste them. Ctrl+C/X already snapshotted them a moment ago; a copy from elsewhere
    // (the browser menu) snapshots here. Viewers may copy; cutting needs edit rights.
    const onCopyCut = (e: ClipboardEvent) => {
      if (activePane !== pane || isTextTarget(e.target)) return;
      if (e.type === 'cut' && readOnly) return;
      let payload = recentClipboard(1500);
      if (!payload) {
        const ids = [...pane.getState().selection];
        if (!ids.length) return;
        payload = snapshotForClipboard(board, ids);
        rememberClipboard(payload);
        if (e.type === 'cut') deleteSelection(ctx);
      }
      writeToClipboardEvent(e, payload);
      e.preventDefault();
    };

    const onBlur = () => pane.getState().setSpaceHeld(false);

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('paste', onPaste);
    window.addEventListener('copy', onCopyCut);
    window.addEventListener('cut', onCopyCut);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('copy', onCopyCut);
      window.removeEventListener('cut', onCopyCut);
      window.removeEventListener('blur', onBlur);
    };
  }, [board, pane, readOnly]);
}
